import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useFlags } from "launchdarkly-react-client-sdk";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getIdpAdminClient } from "sj-ui-toolkit/src/client";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { IS_DEV_MODE } from "sj-ui-toolkit/src/constants/env";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Entitlement carried by MCP API keys; Key Management labels these "MCP Key".
const MCP_ENTITLEMENT = "mcp";

// Server URLs admins paste into their MCP connector config. OAuth (Claude)
// traffic is served on the my. host, API-key traffic on the api. host.
const MCP_OAUTH_SERVER_URL = IS_DEV_MODE
  ? "https://my.securityjourney.dev/mcp"
  : "https://my.securityjourney.com/mcp";
const MCP_API_SERVER_URL = IS_DEV_MODE
  ? "https://api.securityjourney.dev/mcp"
  : "https://api.securityjourney.com/mcp";

// Claude connects with a tenant-owned OAuth client (svc-idp); OpenAI connects
// with an API key (svc-api-tokens). svc-idp stores no provider field, so the
// provider choice only selects which credential type the wizard creates.
type McpProvider = "claude" | "openai";

const PROVIDER_OPTIONS: { label: string; value: McpProvider }[] = [
  { label: "Claude", value: "claude" },
  { label: "OpenAI", value: "openai" },
];

// The auth method is fixed per provider today, so the wizard locks the field
// and explains why in a tooltip.
const AUTH_TYPE_OPTIONS = [
  { label: "OAuth Client", value: "oauth" },
  { label: "API Key", value: "api_key" },
];

const AUTH_TYPE_BY_PROVIDER: Record<McpProvider, string> = {
  claude: "oauth",
  openai: "api_key",
};

const AUTH_TYPE_LOCK_REASON: Record<McpProvider, string> = {
  claude: "Claude only supports OAuth right now — API key support is in beta.",
  openai: "OpenAI connectors only accept an API key.",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OauthClientRow {
  clientId?: string;
  clientName?: string;
  ownerType?: string;
  ownerId?: string;
  createdAt?: string;
  deletedAt?: string;
}

interface ApiKeyRow {
  uuid?: string;
  label?: string;
  createdAt?: string;
  resolvedAt?: string;
}

type ShowToast = (
  severity: "success" | "info" | "warn" | "error",
  summary: string,
  detail: string,
  life?: number
) => void;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SectionEmptyState({ icon, title, hint }: { icon: string; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center gap-1 py-8 text-center">
      <i className={`pi ${icon} text-3xl text-secondary mb-2`} aria-hidden="true" />
      <p className="font-semibold m-0">{title}</p>
      <p className="text-secondary body-sm m-0">{hint}</p>
    </div>
  );
}

const createdAtBody = (rowData: { createdAt?: string }) => {
  if (!rowData.createdAt) return "--";
  return (
    <span title={humanDate(rowData.createdAt)}>{humanDateWithoutTime(rowData.createdAt)}</span>
  );
};

// ---------------------------------------------------------------------------
// Claude client card
// ---------------------------------------------------------------------------

function McpClientCard({
  client,
  onDisable,
  onCopy,
}: {
  client: OauthClientRow;
  onDisable: (client: OauthClientRow) => void;
  onCopy: (text: string, what: string) => void;
}) {
  const displayName = client.clientName || client.clientId || "Untitled client";

  return (
    <div
      className="bg-elevation-2 border border-surface-border rounded-lg p-4 flex flex-col gap-3 h-full"
      data-testid={`mcp-client-card-${client.clientId}`}
    >
      <div className="font-semibold text-lg truncate" title={displayName}>
        {displayName}
      </div>
      <div>
        <div className="body-xs text-muted mb-1">Client ID</div>
        <div className="flex items-center gap-1">
          <span className="font-mono body-sm break-all">{client.clientId}</span>
          <Button
            icon="pi pi-copy"
            text
            size="small"
            onClick={() => onCopy(client.clientId ?? "", "Client ID")}
            tooltip="Copy to clipboard"
            tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
            aria-label="Copy client ID"
          />
        </div>
      </div>
      <div>
        <div className="body-xs text-muted mb-1">Server URL</div>
        <div className="flex items-center gap-1">
          <span className="font-mono body-sm break-all">{MCP_OAUTH_SERVER_URL}</span>
          <Button
            icon="pi pi-copy"
            text
            size="small"
            onClick={() => onCopy(MCP_OAUTH_SERVER_URL, "Server URL")}
            tooltip="Copy to clipboard"
            tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
            aria-label="Copy server URL"
          />
        </div>
      </div>
      <div className="flex-1" />
      <div className="flex justify-between items-center gap-2">
        <span
          className="body-xs text-muted"
          title={client.createdAt ? humanDate(client.createdAt) : undefined}
        >
          {client.createdAt ? `Created ${humanDateWithoutTime(client.createdAt)}` : "--"}
        </span>
        <Button
          label="Disable"
          severity="danger"
          text
          size="small"
          onClick={() => onDisable(client)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add MCP connection dialog (shared wizard for both credential types)
// ---------------------------------------------------------------------------

function AddMcpConnectionDialog({
  visible,
  existingClientCount,
  apiKeysEnabled,
  onHide,
  onClientCreated,
  onKeyCreated,
  showToast,
}: {
  visible: boolean;
  existingClientCount: number;
  apiKeysEnabled: boolean;
  onHide: () => void;
  onClientCreated: (client: OauthClientRow) => void;
  onKeyCreated: () => void;
  showToast: ShowToast;
}) {
  const idpAdminClient = getIdpAdminClient();
  const apiTokensClient = getApiTokensClient();
  const tenant = getCurrentSelectedTenant();

  const [phase, setPhase] = useState<"form" | "success">("form");
  const [provider, setProvider] = useState<McpProvider>("claude");
  const [displayName, setDisplayName] = useState("");
  const [keyName, setKeyName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdClientId, setCreatedClientId] = useState("");
  const [createdKeySecret, setCreatedKeySecret] = useState("");

  // With mcp-auth-show-api-key off, OpenAI isn't offered and the wizard always
  // behaves as Claude — even if the flag flips while the dialog is open.
  const activeProvider: McpProvider = apiKeysEnabled ? provider : "claude";
  const providerOptions = apiKeysEnabled
    ? PROVIDER_OPTIONS
    : PROVIDER_OPTIONS.filter(o => o.value === "claude");

  const handleHide = () => {
    setPhase("form");
    setProvider("claude");
    setDisplayName("");
    setKeyName("");
    setCreatedClientId("");
    setCreatedKeySecret("");
    onHide();
  };

  const handleCreate = async () => {
    if (activeProvider === "openai") {
      if (!keyName.trim()) return;
      setIsSubmitting(true);
      try {
        const { data, error } = await apiTokensClient.issueApiToken(keyName.trim(), [
          MCP_ENTITLEMENT,
        ]);
        if (error) throw error;
        setCreatedKeySecret(data?.secret ?? "");
        setKeyName("");
        onKeyCreated();
        setPhase("success");
      } catch {
        showToast("error", "Error", "Failed to create the API key. Please try again.", 5000);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!tenant) return;
    setIsSubmitting(true);
    try {
      const { data, error } = await idpAdminClient.createOAuthClientRegistration(
        tenant,
        displayName.trim() || undefined
      );
      if (error) throw error;

      setCreatedClientId(data?.client?.clientId ?? "");
      setDisplayName("");
      if (data?.client) onClientCreated(data.client);
      setPhase("success");
    } catch (error) {
      showToast(
        "error",
        "Error",
        error instanceof Error && error.message
          ? error.message
          : "Failed to create the MCP connection. Please try again.",
        5000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = (text: string, what: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      showToast("info", "Copied", `${what} copied to clipboard`);
    }
  };

  const createDisabled = isSubmitting || (activeProvider === "openai" && !keyName.trim());

  return (
    <Dialog
      pt={{ root: { className: "border-none" } }}
      header="Add MCP Connection"
      visible={visible}
      onHide={handleHide}
      closable={!isSubmitting}
      closeOnEscape={!isSubmitting}
      style={{ width: "40rem", maxWidth: "95vw" }}
      modal
      footer={
        phase === "form" ? (
          <div className="flex gap-2 justify-end">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={handleHide}
              disabled={isSubmitting}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleCreate}
              disabled={createDisabled}
            />
          </div>
        ) : (
          <div className="flex gap-2 justify-end">
            <Button label="Done" onClick={handleHide} />
          </div>
        )
      }
    >
      {phase === "form" ? (
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="mcp-provider" className="block mb-2">
              MCP Provider
            </label>
            <Dropdown
              inputId="mcp-provider"
              value={activeProvider}
              onChange={e => setProvider(e.value as McpProvider)}
              options={providerOptions}
              className="w-full"
              checkmark
              highlightOnSelect={false}
              disabled={isSubmitting || !apiKeysEnabled}
            />
            <p className="body-xs text-secondary m-0 mt-1">More providers coming soon.</p>
          </div>

          <div>
            <label htmlFor="mcp-auth-type" className="block mb-2">
              Authentication
            </label>
            <Dropdown
              inputId="mcp-auth-type"
              value={AUTH_TYPE_BY_PROVIDER[activeProvider]}
              options={AUTH_TYPE_OPTIONS}
              className="w-full"
              disabled
              tooltip={AUTH_TYPE_LOCK_REASON[activeProvider]}
              tooltipOptions={{ showOnDisabled: true, position: "top" }}
            />
          </div>

          {activeProvider === "claude" ? (
            <div>
              <label htmlFor="mcp-display-name" className="block mb-2">
                Display Name
              </label>
              <InputText
                id="mcp-display-name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="e.g., Claude"
                className="w-full"
                maxLength={120}
                disabled={isSubmitting}
              />
              <p className="body-xs text-secondary m-0 mt-1">
                Optional — helps tell connections apart.
              </p>
            </div>
          ) : (
            <div>
              <label htmlFor="mcp-key-name" className="block mb-2">
                Key Name
              </label>
              <InputText
                id="mcp-key-name"
                value={keyName}
                onChange={e => setKeyName(e.target.value)}
                placeholder="e.g., Jane — ChatGPT"
                className="w-full"
                maxLength={120}
                disabled={isSubmitting}
              />
              <p className="body-xs text-secondary m-0 mt-1">
                Each admin should create their own key — include your name so it's easy to tell
                whose is whose.
              </p>
            </div>
          )}

          {activeProvider === "claude" && existingClientCount > 0 && (
            <Message
              severity="warn"
              text={`You already have ${existingClientCount} active Claude connection${
                existingClientCount !== 1 ? "s" : ""
              }. Most setups need only one — create another only if you're connecting a separate tool.`}
            />
          )}
        </div>
      ) : activeProvider === "claude" ? (
        <div className="flex flex-col gap-3">
          <Message severity="success" text="MCP connection created." />
          <div>
            <label htmlFor="mcp-new-client-id" className="block mb-2">
              Client ID
            </label>
            <div className="p-inputgroup">
              <InputText
                id="mcp-new-client-id"
                value={createdClientId}
                readOnly
                className="font-mono"
              />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(createdClientId, "Client ID")}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
                aria-label="Copy client ID"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label htmlFor="mcp-new-server-url" className="block mb-2">
              Server URL
            </label>
            <div className="p-inputgroup">
              <InputText
                id="mcp-new-server-url"
                value={MCP_OAUTH_SERVER_URL}
                readOnly
                className="font-mono"
              />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(MCP_OAUTH_SERVER_URL, "Server URL")}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
                aria-label="Copy server URL"
              />
            </div>
          </div>
          <p className="m-0 text-secondary body-sm">
            In Claude, go to Settings → Connectors → Add custom connector, then enter this server
            URL and client ID. You can copy both again anytime from the connection&apos;s card.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Message
            severity="success"
            text="API key created. Copy it now — it will not be shown again."
          />
          <div>
            <label htmlFor="mcp-new-api-key" className="block mb-2">
              API Key
            </label>
            <div className="p-inputgroup">
              <InputText
                id="mcp-new-api-key"
                value={createdKeySecret}
                readOnly
                className="font-mono"
              />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(createdKeySecret, "API key")}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
                aria-label="Copy API key"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label htmlFor="mcp-new-key-server-url" className="block mb-2">
              Server URL
            </label>
            <div className="p-inputgroup">
              <InputText
                id="mcp-new-key-server-url"
                value={MCP_API_SERVER_URL}
                readOnly
                className="font-mono"
              />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(MCP_API_SERVER_URL, "Server URL")}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
                aria-label="Copy server URL"
              />
            </div>
          </div>
          <p className="m-0 text-secondary body-sm">
            Use this server URL and key in your MCP tool's connector configuration. If you lose the
            key, delete it and create a new one.
          </p>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// OpenAI section (svc-api-tokens keys with the mcp entitlement)
// ---------------------------------------------------------------------------

function OpenAiKeysSection({
  showToast,
  refreshNonce,
}: {
  showToast: ShowToast;
  refreshNonce: number;
}) {
  const apiTokensClient = getApiTokensClient();

  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await apiTokensClient.listApiTokensWithEntitlements([
        MCP_ENTITLEMENT,
      ]);
      if (error) throw error;
      setApiKeys(data?.apiTokens ?? []);
    } catch {
      showToast("error", "Error", "Failed to load API keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, apiTokensClient]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys, refreshNonce]);

  const sortedKeys = useMemo(
    () =>
      [...apiKeys].sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      ),
    [apiKeys]
  );

  const confirmDelete = (key: ApiKeyRow) => {
    confirmDialog({
      message: `Are you sure you want to delete "${key.label || key.uuid}"? MCP tools using this key will no longer be able to connect. This action cannot be undone.`,
      header: "Delete API Key",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      style: { width: "32rem", maxWidth: "95vw" },
      accept: async () => {
        if (!key.uuid) return;
        try {
          const { error } = await apiTokensClient.revokeApiToken(key.uuid);
          if (error) throw error;
          setApiKeys(prev => prev.filter(k => k.uuid !== key.uuid));
          showToast("info", "Deleted", "API key deleted successfully");
        } catch {
          showToast("error", "Error", "Failed to delete API key");
        }
      },
    });
  };

  return (
    <Card>
      <h2 className="atlas-h4 m-0">OpenAI</h2>
      <p className="m-0 mt-2 text-secondary">
        API keys for MCP tools that connect with a key, such as ChatGPT connectors. Each admin
        should create their own key. Keys are shown once when created.
      </p>

      <div className="mt-3">
        <div className="body-xs text-muted mb-1">Server URL</div>
        <div className="flex items-center gap-1">
          <span className="font-mono body-sm break-all">{MCP_API_SERVER_URL}</span>
          <Button
            icon="pi pi-copy"
            text
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(MCP_API_SERVER_URL);
              showToast("info", "Copied", "Server URL copied to clipboard");
            }}
            tooltip="Copy to clipboard"
            tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
            aria-label="Copy server URL"
          />
        </div>
      </div>

      <div className="mt-4">
        {!isLoading && sortedKeys.length === 0 ? (
          <SectionEmptyState
            icon="pi-key"
            title="No API keys yet"
            hint="Use the Add MCP Connection button above and choose OpenAI to create one."
          />
        ) : (
          <DataTable
            value={sortedKeys}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            sortMode="single"
            loading={isLoading}
            emptyMessage="Loading API keys..."
          >
            <Column
              field="label"
              header="Name"
              body={(rowData: ApiKeyRow) => rowData.label || rowData.uuid}
              sortable
            />
            <Column field="createdAt" header="Created At" body={createdAtBody} sortable />
            <Column
              field="resolvedAt"
              header="Last Used"
              body={(rowData: ApiKeyRow) => {
                if (!rowData.resolvedAt) return "Never";
                return (
                  <span title={humanDate(rowData.resolvedAt)}>
                    {humanDateWithoutTime(rowData.resolvedAt)}
                  </span>
                );
              }}
              sortable
            />
            <Column
              header="Action"
              body={(rowData: ApiKeyRow) => (
                <Button
                  label="Delete"
                  severity="danger"
                  text
                  size="small"
                  disabled={isLoading}
                  onClick={() => confirmDelete(rowData)}
                />
              )}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const McpConnection = () => {
  useBreadcrumb({
    label: "MCP",
    route: AdminAppRoutes.routes.connections.routes.mcp.route,
    display: "page",
  });

  const { mcpOauthClients, mcpAuthShowApiKey } = useFlags();
  const navigate = useNavigate();

  const apiKeysEnabled = !!mcpAuthShowApiKey; // Declared like this for easier typing

  const toast = useRef<Toast>(null);
  const idpAdminClient = getIdpAdminClient();
  const tenant = getCurrentSelectedTenant();

  const [clients, setClients] = useState<OauthClientRow[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [clientsLoadError, setClientsLoadError] = useState(false);
  const [isAddDialogVisible, setIsAddDialogVisible] = useState(false);
  const [keysRefreshNonce, setKeysRefreshNonce] = useState(0);

  const showLocalToast = useCallback<ShowToast>((severity, summary, detail, life = 3000) => {
    toast.current?.show({ severity, summary, detail, life });
  }, []);

  const loadClients = useCallback(async () => {
    if (!tenant) {
      setIsLoadingClients(false);
      return;
    }
    setIsLoadingClients(true);
    setClientsLoadError(false);
    try {
      const { data, error } = await idpAdminClient.listOAuthClientRegistrations(tenant);
      if (error) throw error;
      setClients((data?.clients ?? []).filter(c => !c.deletedAt));
    } catch {
      setClientsLoadError(true);
      showLocalToast("error", "Error", "Failed to load Claude connections.", 5000);
    } finally {
      setIsLoadingClients(false);
    }
  }, [showLocalToast, idpAdminClient, tenant]);

  // svc-idp rejects every MCP call while mcp-oauth-clients is off, so direct
  // navigation gets bounced; while LaunchDarkly hasn't resolved, hold and don't
  // fetch (matches the /login/mcp pattern from SHRPA-4305).
  useEffect(() => {
    if (mcpOauthClients === false) {
      navigate(AdminAppRoutes.routes.connections.route, { replace: true });
    }
  }, [mcpOauthClients, navigate]);

  useEffect(() => {
    if (mcpOauthClients === true) loadClients();
  }, [loadClients, mcpOauthClients]);

  const sortedClients = useMemo(
    () =>
      [...clients].sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      ),
    [clients]
  );

  // Optimistically show the new client, then reconcile with the server without
  // flipping the grid back into its loading state.
  const handleClientCreated = useCallback(
    (client: OauthClientRow) => {
      setClients(prev => [client, ...prev]);
      if (!tenant) return;
      idpAdminClient
        .listOAuthClientRegistrations(tenant)
        .then(({ data, error }) => {
          if (error) throw error;
          setClients((data?.clients ?? []).filter(c => !c.deletedAt));
        })
        .catch(() => {
          showLocalToast(
            "warn",
            "Warning",
            "Connection created, but the list may be out of date. Reload the page to refresh it.",
            5000
          );
        });
    },
    [idpAdminClient, tenant, showLocalToast]
  );

  const handleKeyCreated = useCallback(() => {
    setKeysRefreshNonce(n => n + 1);
  }, []);

  const handleCopy = useCallback(
    (text: string, field: string) => {
      if (text) {
        navigator.clipboard.writeText(text);
        showLocalToast("info", "Copied", `${field} copied to clipboard`);
      }
    },
    [showLocalToast]
  );

  const confirmDisable = useCallback(
    (client: OauthClientRow) => {
      confirmDialog({
        message: `Are you sure you want to disable "${client.clientName || client.clientId}"? Claude connections using this client ID will no longer be able to sign in. This action cannot be undone.`,
        header: "Disable Connection",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        style: { width: "32rem", maxWidth: "95vw" },
        accept: async () => {
          if (!tenant || !client.clientId) return;
          try {
            const { error } = await idpAdminClient.disableOAuthClientRegistration(
              tenant,
              client.clientId
            );
            if (error) throw error;
            setClients(prev => prev.filter(c => c.clientId !== client.clientId));
            showLocalToast("info", "Disabled", "Connection disabled successfully");
          } catch {
            showLocalToast("error", "Error", "Failed to disable the connection");
          }
        },
      });
    },
    [idpAdminClient, tenant, showLocalToast]
  );

  if (mcpOauthClients !== true) {
    return (
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1>MCP</h1>
          <Tag value="Beta" severity="info" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} width="100%" height="160px" borderRadius="12px" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
        <div className="flex-1 min-w-64">
          <div className="flex items-center gap-3">
            <h1>MCP</h1>
            <Tag value="Beta" severity="info" />
          </div>
          <p className="m-0 mt-2 text-secondary">
            {apiKeysEnabled
              ? "Connect MCP tools like Claude and ChatGPT to Security Journey."
              : "Connect MCP tools like Claude to Security Journey."}
          </p>
        </div>
        <Button
          label="Add MCP Connection"
          icon="pi pi-plus"
          className="flex-shrink-0 whitespace-nowrap"
          onClick={() => setIsAddDialogVisible(true)}
          disabled={isLoadingClients}
        />
      </div>

      <div className="flex flex-col gap-4 mb-8">
        <Card>
          <h2 className="atlas-h4 m-0">Claude</h2>
          <p className="m-0 mt-2 text-secondary">
            Claude connects with an OAuth client — users sign in with their Security Journey
            account. Give the client ID to Claude to start the sign-in flow.
          </p>
          <div className="mt-4">
            {isLoadingClients ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} width="100%" height="160px" borderRadius="12px" />
                ))}
              </div>
            ) : clientsLoadError && sortedClients.length === 0 ? (
              <div className="flex flex-col items-start gap-3">
                <Message severity="error" text="Failed to load Claude connections." />
                <Button
                  label="Retry"
                  icon="pi pi-refresh"
                  outlined
                  size="small"
                  onClick={loadClients}
                />
              </div>
            ) : sortedClients.length === 0 ? (
              <SectionEmptyState
                icon="pi-microchip-ai"
                title="No Claude connections yet"
                hint="Use the Add MCP Connection button above to connect Claude."
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {sortedClients.map(client => (
                  <McpClientCard
                    key={client.clientId}
                    client={client}
                    onDisable={confirmDisable}
                    onCopy={handleCopy}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>

        {apiKeysEnabled && (
          <OpenAiKeysSection showToast={showLocalToast} refreshNonce={keysRefreshNonce} />
        )}
      </div>

      <AddMcpConnectionDialog
        visible={isAddDialogVisible}
        existingClientCount={clients.length}
        apiKeysEnabled={apiKeysEnabled}
        onHide={() => setIsAddDialogVisible(false)}
        onClientCreated={handleClientCreated}
        onKeyCreated={handleKeyCreated}
        showToast={showLocalToast}
      />
    </>
  );
};

export default McpConnection;
